/**
 * OptimierungFieldRow — atomare Einheit des pro-Feld-Modells (Claude-Design drawer-v2).
 * Jede Zeile ist eigenständig auslösbar; der Button trägt seine eigenen Credit-Kosten.
 */
import { AlertTriangle, Check, Loader2, Sparkles } from 'lucide-react'

import { DV2, type FieldKind } from './tokens'

export interface OptimierungFieldRowProps {
  kind: FieldKind
  name: string
  status: string
  reason: string
  cost: number
  rowState?: 'open' | 'busy' | 'done'
  busyText?: string
  onOptimize?: () => void
}

const ICON = { new: Sparkles, done: Check, err: AlertTriangle, warn: AlertTriangle }
const TAG_FG: Record<FieldKind, string> = {
  err: DV2.remFg,
  warn: DV2.warnFg,
  new: DV2.orange,
  done: DV2.addFg,
}

export function OptimierungFieldRow({
  kind,
  name,
  status,
  reason,
  cost,
  rowState = 'open',
  busyText = 'KI schreibt …',
  onOptimize,
}: OptimierungFieldRowProps) {
  const Icon = ICON[kind]
  const proaktiv = kind === 'new'
  return (
    <div
      className={`flex items-center gap-3 border-t ${DV2.line} px-3.5 py-3 ${
        proaktiv ? 'bg-[rgba(255,107,0,0.04)]' : ''
      } ${rowState === 'busy' ? 'opacity-90' : ''}`}
    >
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center ${TAG_FG[kind]}`}>
        <Icon size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-sm-minus font-semibold ${DV2.fg1}`}>{name}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs-plus">
          {rowState === 'busy' ? (
            <span className={`flex items-center gap-1.5 ${DV2.fg3}`}>
              <Loader2 size={11} className="animate-spin" />
              {busyText}
            </span>
          ) : (
            <>
              <span className={`font-semibold ${TAG_FG[kind]}`}>{status}</span>
              <span className={DV2.fg4}>{reason}</span>
            </>
          )}
        </div>
      </div>
      {rowState === 'open' && (
        <button
          type="button"
          onClick={onOptimize}
          className={`flex shrink-0 items-center gap-1.5 rounded-md border ${DV2.lineStrong} ${DV2.card2} px-2.5 py-1.5 text-xs font-semibold ${DV2.fg1} transition hover:border-[rgba(255,107,0,0.4)]`}
        >
          <Sparkles size={12} className="text-primary-500" />
          Optimieren
          <span className={`ml-1 font-mono text-2xs ${DV2.fg4}`}>{cost} Cr</span>
        </button>
      )}
      {rowState === 'busy' && (
        <span className={`flex shrink-0 items-center gap-1.5 text-xs ${DV2.fg3}`}>
          <Loader2 size={12} className="animate-spin" />
          schreibt…
        </span>
      )}
      {rowState === 'done' && (
        <span className={`flex shrink-0 items-center gap-1.5 text-xs ${DV2.addFg}`}>
          <Check size={12} />
          übernommen
        </span>
      )}
    </div>
  )
}
